import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

// ────────────────────────────────────────────────────────────────────────────
// Translations (keep keys in sync — every key MUST exist in all 3 languages)
// ────────────────────────────────────────────────────────────────────────────
const dictionaries = {
  en: {
    "brand.tagline": "Retro TCG Vault",
    "header.search.placeholder": "SEARCH POKÉMON...",
    "header.login": "LOGIN",
    "header.theme.dark": "DARK",
    "header.theme.light": "LIGHT",

    "hero.badge": "THE WORLDWIDE TRADING CARDS POKÉDEX",
    "hero.title.line1": "CATCH'EM.",
    "hero.title.line2": "TRADE'EM.",
    "hero.subtitle.prefix": "1025 Pokémon. Thousands of trading cards. Real-time prices from",
    "hero.subtitle.and": "&",
    "hero.cta.explore": "▶ EXPLORE POKÉDEX",
    "hero.cta.highscores": "☆ HIGH SCORES",
    "hero.stats.pokemon": "Pokémon",
    "hero.stats.cards": "Cards",
    "hero.stats.prices": "Prices",

    "trending.live": "LIVE",

    "filters.title": "POKÉDEX FILTERS",
    "filters.show": "▼ SHOW",
    "filters.hide": "▲ HIDE",
    "filters.found": "FOUND {n} POKÉMON",
    "filters.search.label": "⌕ Search Name / ID",
    "filters.search.placeholder": "Pikachu or 025",
    "filters.type.label": "⚡ Type",
    "filters.type.all": "All Types",
    "filters.gen.label": "🎮 Generation",
    "filters.gen.all": "All Generations",
    "filters.minId": "Min ID",
    "filters.maxId": "Max ID",
    "filters.sort.label": "Sort By",
    "filters.sort.id": "ID Number",
    "filters.sort.nameAsc": "Name (A-Z)",
    "filters.sort.nameDesc": "Name (Z-A)",
    "filters.clear": "✖ CLEAR",

    "pagination.prev": "◀ PREV",
    "pagination.next": "NEXT ▶",

    "noresults.title": "▌▌ GAME OVER ▌▌",
    "noresults.subtitle": "No Pokémon match those filters",

    "loading.label": "Catching all 1025 Pokémon...",

    "modal.tab.info": "▣ POKÉMON INFO",
    "modal.tab.cards": "▦ TRADING CARDS",
    "modal.info.basic": "▣ BASIC INFO",
    "modal.info.height": "Height",
    "modal.info.weight": "Weight",
    "modal.info.baseXp": "Base XP",
    "modal.info.types": "▣ TYPES",
    "modal.info.stats": "▣ STATS",
    "modal.info.abilities": "▣ ABILITIES",
    "modal.info.loading": "► LOADING ◄",
    "modal.info.error": "▌▌ DATA LOST ▌▌",
    "modal.dex": "National Pokédex",

    "cards.loadPrices": "$ LOAD REAL-TIME PRICES",
    "cards.loadingPrices": "◴ LOADING PRICES...",
    "cards.allLoaded": "◉ ALL PRICES LOADED (Market • Low • Mid • High)",
    "cards.fetchingNote": "⚡ FETCHING PRICES IN REAL-TIME...",
    "cards.sort": "Sort:",
    "cards.sort.default": "Default",
    "cards.sort.priceHigh": "Price (High → Low)",
    "cards.sort.priceLow": "Price (Low → High)",
    "cards.loading": "► LOADING CARDS ◄",
    "cards.loadingSub": "Scanning the vault for {name}...",
    "cards.found": "◉ FOUND {n} CARDS FOR {name}",
    "cards.noPrice": "▌ NO PRICE DATA ▌",
    "cards.none.title": "▌▌ NO CARDS ▌▌",
    "cards.none.subtitle": "{name} has not yet been released as a trading card.",

    "highscores.title": "☆ HIGH SCORES ☆",
    "highscores.subtitle": "Most valuable cards across {n} Pokémon",
    "highscores.loading": "► SCANNING THE VAULT ◄",
    "highscores.loadingSub": "Ranking the most valuable cards across all famous Pokémon...",
    "highscores.error": "Couldn't load high scores.",

    "profile.title.your": "▣ YOUR TRAINER",
    "profile.title.login": "▣ LOGIN",
    "profile.title.register": "▣ NEW TRAINER",
    "profile.tab.login": "LOGIN",
    "profile.tab.register": "REGISTER",
    "profile.input.name": "TRAINER NAME",
    "profile.input.email": "EMAIL",
    "profile.input.password": "PASSWORD",
    "profile.input.bio": "BIO (OPTIONAL)",
    "profile.input.location": "LOCATION (OPTIONAL)",
    "profile.submit.login": "▶ LOGIN",
    "profile.submit.register": "▶ CREATE ACCOUNT",
    "profile.logout": "▶ LOGOUT",
    "profile.stat.level": "Level",
    "profile.stat.badges": "Badges",
    "profile.stat.pokemon": "Pokémon",
    "profile.stat.cards": "Cards",
  },

  it: {
    "brand.tagline": "Caveau Retro TCG",
    "header.search.placeholder": "CERCA POKÉMON...",
    "header.login": "ACCEDI",
    "header.theme.dark": "SCURO",
    "header.theme.light": "CHIARO",

    "hero.badge": "IL POKÉDEX MONDIALE DELLE CARTE COLLEZIONABILI",
    "hero.title.line1": "CATTURALI.",
    "hero.title.line2": "SCAMBIALI.",
    "hero.subtitle.prefix": "1025 Pokémon. Migliaia di carte collezionabili. Prezzi in tempo reale da",
    "hero.subtitle.and": "e",
    "hero.cta.explore": "▶ ESPLORA POKÉDEX",
    "hero.cta.highscores": "☆ TOP PREZZI",
    "hero.stats.pokemon": "Pokémon",
    "hero.stats.cards": "Carte",
    "hero.stats.prices": "Prezzi",

    "trending.live": "LIVE",

    "filters.title": "FILTRI POKÉDEX",
    "filters.show": "▼ MOSTRA",
    "filters.hide": "▲ NASCONDI",
    "filters.found": "TROVATI {n} POKÉMON",
    "filters.search.label": "⌕ Cerca Nome / ID",
    "filters.search.placeholder": "Pikachu o 025",
    "filters.type.label": "⚡ Tipo",
    "filters.type.all": "Tutti i Tipi",
    "filters.gen.label": "🎮 Generazione",
    "filters.gen.all": "Tutte le Generazioni",
    "filters.minId": "ID Min",
    "filters.maxId": "ID Max",
    "filters.sort.label": "Ordina Per",
    "filters.sort.id": "Numero ID",
    "filters.sort.nameAsc": "Nome (A-Z)",
    "filters.sort.nameDesc": "Nome (Z-A)",
    "filters.clear": "✖ AZZERA",

    "pagination.prev": "◀ PREC",
    "pagination.next": "SUCC ▶",

    "noresults.title": "▌▌ GAME OVER ▌▌",
    "noresults.subtitle": "Nessun Pokémon corrisponde ai filtri",

    "loading.label": "Cattura di tutti i 1025 Pokémon...",

    "modal.tab.info": "▣ SCHEDA POKÉMON",
    "modal.tab.cards": "▦ CARTE COLLEZIONABILI",
    "modal.info.basic": "▣ INFO BASE",
    "modal.info.height": "Altezza",
    "modal.info.weight": "Peso",
    "modal.info.baseXp": "XP Base",
    "modal.info.types": "▣ TIPI",
    "modal.info.stats": "▣ STATISTICHE",
    "modal.info.abilities": "▣ ABILITÀ",
    "modal.info.loading": "► CARICAMENTO ◄",
    "modal.info.error": "▌▌ DATI PERSI ▌▌",
    "modal.dex": "Pokédex Nazionale",

    "cards.loadPrices": "$ CARICA PREZZI LIVE",
    "cards.loadingPrices": "◴ CARICAMENTO PREZZI...",
    "cards.allLoaded": "◉ PREZZI CARICATI (Mercato • Basso • Medio • Alto)",
    "cards.fetchingNote": "⚡ AGGIORNAMENTO PREZZI IN TEMPO REALE...",
    "cards.sort": "Ordina:",
    "cards.sort.default": "Predefinito",
    "cards.sort.priceHigh": "Prezzo (Alto → Basso)",
    "cards.sort.priceLow": "Prezzo (Basso → Alto)",
    "cards.loading": "► CARICAMENTO CARTE ◄",
    "cards.loadingSub": "Scansione del caveau per {name}...",
    "cards.found": "◉ TROVATE {n} CARTE PER {name}",
    "cards.noPrice": "▌ PREZZO N/D ▌",
    "cards.none.title": "▌▌ NESSUNA CARTA ▌▌",
    "cards.none.subtitle": "{name} non ha ancora una carta collezionabile.",

    "highscores.title": "☆ TOP PREZZI ☆",
    "highscores.subtitle": "Carte di maggior valore tra {n} Pokémon",
    "highscores.loading": "► SCANSIONE DEL CAVEAU ◄",
    "highscores.loadingSub": "Classifica delle carte più preziose tra tutti i Pokémon famosi...",
    "highscores.error": "Impossibile caricare la classifica.",

    "profile.title.your": "▣ IL TUO ALLENATORE",
    "profile.title.login": "▣ ACCEDI",
    "profile.title.register": "▣ NUOVO ALLENATORE",
    "profile.tab.login": "ACCEDI",
    "profile.tab.register": "REGISTRATI",
    "profile.input.name": "NOME ALLENATORE",
    "profile.input.email": "EMAIL",
    "profile.input.password": "PASSWORD",
    "profile.input.bio": "BIO (OPZIONALE)",
    "profile.input.location": "LOCALITÀ (OPZIONALE)",
    "profile.submit.login": "▶ ACCEDI",
    "profile.submit.register": "▶ CREA ACCOUNT",
    "profile.logout": "▶ ESCI",
    "profile.stat.level": "Livello",
    "profile.stat.badges": "Medaglie",
    "profile.stat.pokemon": "Pokémon",
    "profile.stat.cards": "Carte",
  },

  es: {
    "brand.tagline": "Bóveda Retro TCG",
    "header.search.placeholder": "BUSCAR POKÉMON...",
    "header.login": "ENTRAR",
    "header.theme.dark": "OSCURO",
    "header.theme.light": "CLARO",

    "hero.badge": "LA POKÉDEX MUNDIAL DE CARTAS COLECCIONABLES",
    "hero.title.line1": "ATRÁPALOS.",
    "hero.title.line2": "INTERCÁMBIALOS.",
    "hero.subtitle.prefix": "1025 Pokémon. Miles de cartas coleccionables. Precios en tiempo real de",
    "hero.subtitle.and": "y",
    "hero.cta.explore": "▶ EXPLORAR POKÉDEX",
    "hero.cta.highscores": "☆ TOP PRECIOS",
    "hero.stats.pokemon": "Pokémon",
    "hero.stats.cards": "Cartas",
    "hero.stats.prices": "Precios",

    "trending.live": "LIVE",

    "filters.title": "FILTROS POKÉDEX",
    "filters.show": "▼ MOSTRAR",
    "filters.hide": "▲ OCULTAR",
    "filters.found": "ENCONTRADOS {n} POKÉMON",
    "filters.search.label": "⌕ Buscar Nombre / ID",
    "filters.search.placeholder": "Pikachu o 025",
    "filters.type.label": "⚡ Tipo",
    "filters.type.all": "Todos los Tipos",
    "filters.gen.label": "🎮 Generación",
    "filters.gen.all": "Todas las Generaciones",
    "filters.minId": "ID Mín",
    "filters.maxId": "ID Máx",
    "filters.sort.label": "Ordenar Por",
    "filters.sort.id": "Número ID",
    "filters.sort.nameAsc": "Nombre (A-Z)",
    "filters.sort.nameDesc": "Nombre (Z-A)",
    "filters.clear": "✖ LIMPIAR",

    "pagination.prev": "◀ ANT",
    "pagination.next": "SIG ▶",

    "noresults.title": "▌▌ GAME OVER ▌▌",
    "noresults.subtitle": "Ningún Pokémon coincide con los filtros",

    "loading.label": "Atrapando los 1025 Pokémon...",

    "modal.tab.info": "▣ INFO POKÉMON",
    "modal.tab.cards": "▦ CARTAS COLECCIONABLES",
    "modal.info.basic": "▣ INFO BÁSICA",
    "modal.info.height": "Altura",
    "modal.info.weight": "Peso",
    "modal.info.baseXp": "XP Base",
    "modal.info.types": "▣ TIPOS",
    "modal.info.stats": "▣ ESTADÍSTICAS",
    "modal.info.abilities": "▣ HABILIDADES",
    "modal.info.loading": "► CARGANDO ◄",
    "modal.info.error": "▌▌ DATOS PERDIDOS ▌▌",
    "modal.dex": "Pokédex Nacional",

    "cards.loadPrices": "$ CARGAR PRECIOS LIVE",
    "cards.loadingPrices": "◴ CARGANDO PRECIOS...",
    "cards.allLoaded": "◉ PRECIOS CARGADOS (Mercado • Bajo • Medio • Alto)",
    "cards.fetchingNote": "⚡ ACTUALIZANDO PRECIOS EN TIEMPO REAL...",
    "cards.sort": "Ordenar:",
    "cards.sort.default": "Predeterminado",
    "cards.sort.priceHigh": "Precio (Alto → Bajo)",
    "cards.sort.priceLow": "Precio (Bajo → Alto)",
    "cards.loading": "► CARGANDO CARTAS ◄",
    "cards.loadingSub": "Escaneando la bóveda para {name}...",
    "cards.found": "◉ ENCONTRADAS {n} CARTAS PARA {name}",
    "cards.noPrice": "▌ SIN PRECIO ▌",
    "cards.none.title": "▌▌ NINGUNA CARTA ▌▌",
    "cards.none.subtitle": "{name} aún no tiene carta coleccionable.",

    "highscores.title": "☆ TOP PRECIOS ☆",
    "highscores.subtitle": "Cartas más valiosas entre {n} Pokémon",
    "highscores.loading": "► ESCANEANDO LA BÓVEDA ◄",
    "highscores.loadingSub": "Clasificando las cartas más valiosas de todos los Pokémon famosos...",
    "highscores.error": "No se pudo cargar la clasificación.",

    "profile.title.your": "▣ TU ENTRENADOR",
    "profile.title.login": "▣ ENTRAR",
    "profile.title.register": "▣ NUEVO ENTRENADOR",
    "profile.tab.login": "ENTRAR",
    "profile.tab.register": "REGISTRARSE",
    "profile.input.name": "NOMBRE DEL ENTRENADOR",
    "profile.input.email": "EMAIL",
    "profile.input.password": "CONTRASEÑA",
    "profile.input.bio": "BIO (OPCIONAL)",
    "profile.input.location": "UBICACIÓN (OPCIONAL)",
    "profile.submit.login": "▶ ENTRAR",
    "profile.submit.register": "▶ CREAR CUENTA",
    "profile.logout": "▶ SALIR",
    "profile.stat.level": "Nivel",
    "profile.stat.badges": "Medallas",
    "profile.stat.pokemon": "Pokémon",
    "profile.stat.cards": "Cartas",
  },
};

export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "EN", flag: "🇬🇧" },
  { code: "it", label: "IT", flag: "🇮🇹" },
  { code: "es", label: "ES", flag: "🇪🇸" },
];

const detectInitial = () => {
  if (typeof window === "undefined") return "en";
  const stored = localStorage.getItem("pixeldex_lang");
  if (stored && dictionaries[stored]) return stored;
  // Always default to English; user can switch via the flag buttons in the header.
  return "en";
};

const LanguageContext = createContext({ lang: "en", setLang: () => {}, t: (k) => k });

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectInitial);

  useEffect(() => {
    localStorage.setItem("pixeldex_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo(() => {
    const dict = dictionaries[lang] || dictionaries.en;
    const t = (key, vars) => {
      let str = dict[key] ?? dictionaries.en[key] ?? key;
      if (vars && typeof str === "string") {
        Object.entries(vars).forEach(([k, v]) => {
          str = str.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
        });
      }
      return str;
    };
    return { lang, setLang: setLangState, t };
  }, [lang]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useT = () => useContext(LanguageContext);

// ────────────────────────────────────────────────────────────────────────────
// Flag switcher button group
// ────────────────────────────────────────────────────────────────────────────
export const LanguageSwitcher = () => {
  const { lang, setLang } = useT();
  return (
    <div
      data-testid="language-switcher"
      className="inline-flex border-[3px] border-ink dark:border-neoncyan bg-white dark:bg-nightmuted shadow-[3px_3px_0_0_#111] dark:shadow-[3px_3px_0_0_#ff007f]"
    >
      {SUPPORTED_LANGUAGES.map((l) => {
        const active = l.code === lang;
        return (
          <button
            key={l.code}
            onClick={() => setLang(l.code)}
            data-testid={`lang-${l.code}`}
            aria-label={`Switch to ${l.label}`}
            title={l.label}
            className={`flex items-center gap-1 px-1.5 sm:px-2 py-1.5 border-r-2 last:border-r-0 border-ink/30 dark:border-neoncyan/30 transition-colors ${
              active
                ? "bg-pokered dark:bg-neonpink text-white"
                : "text-ink dark:text-white hover:bg-cream dark:hover:bg-nightsurface"
            }`}
          >
            <span className="text-base leading-none">{l.flag}</span>
            <span className="hidden sm:inline font-pixel text-base leading-none">{l.label}</span>
          </button>
        );
      })}
    </div>
  );
};
